// FILE: CostCalculatorPage.jsx (FINAL - Corrected Frontend Logic - Retains UI Structure)

import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { FaCalculator } from 'react-icons/fa';
// MAKE SURE THIS IS THE CORRECTED VERSION OF CostBreakdown
import CostBreakdown from '../components/CostComparison/CostBreakdown';
import CostChart from '../components/CostChart'; 
import { generateReport } from '../utils/pdfGenerator'; 
import { stateRequirements } from '../data/stateRequirements'; 
import '../styles/CostComparison.css';
// IMPORT THE CORRECTED VERSION OF ComplexityFactors WITH ALL INPUTS
import ComplexityFactors from '../components/ComplexityFactors';
// Import the corrected calculator logic
import { calculateDetailedRequirements } from '../utils/requirementsCalculator';
import FormInputs from '../components/FormInputs';

const CostCalculatorPage = () => {
  // --- State ---
  // Centralized state for ALL form inputs needed by calculator AND wizard
  const [formData, setFormData] = useState({
    // Basic Inputs
    expectedRevenue: '',
    state: 'TX', // Default state
    totalAssets: '', // Needed for EZ checks
    grantFunding: '', // Needed for audit checks
    // Complexity Factors (ensure these keys match state updated by ComplexityFactors)
    subsector: 'human_services',
    geographicScope: 'local',
    programCount: 1,
    staffingModel: 'volunteer',
    employeeCount: 0,
    inventoryType: ['none'],
    fundingSources: ['individual'],
    hasUnrelatedIncome: false,
    hasForeignAccounts: false,
    hasComplexCompensation: false,
    hasInvestments: false,
    vendorTransactions: '1-10',
    vendorComplexity: ['simple'],
    growthStage: 'startup',
    // Derived factor
    hasInventory: false,
    // Add any other factors your wizard collects
  });

  // State to hold the FULL calculation results object from requirementsCalculator.js
  const [calculations, setCalculations] = useState(null); // Initialize as null

  const [showProjections, setShowProjections] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // --- Data & Options (Retained from your code) ---
  const stateOptions = [
    // { label: "Multi-State...", options: [...] },
    {
      label: "Single State Operation",
      options: Object.entries(stateRequirements)
        .map(([code, state]) => ({ value: code, label: state.name }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }
  ];

  // --- Input Handlers (Retained from your code, ensure names match state) ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'expectedRevenue' || name === 'totalAssets' || name === 'grantFunding') {
      processedValue = value.replace(/[^0-9]/g, '');
    }
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };

  // Updated handler for ComplexityFactors changes
  const handleFactorChange = useCallback((changedFactors) => {
    const inventoryType = changedFactors.inventoryType || formData.inventoryType;
    const hasInventory = Array.isArray(inventoryType) && inventoryType.length > 0 && !inventoryType.includes('none');
    setFormData(prev => ({
      ...prev,
      ...changedFactors,
      hasInventory
    }));
  }, [formData.inventoryType]);

  // --- Calculation Logic (Corrected) ---
  const runCalculation = useCallback(() => {
    setIsCalculating(true);
    const revenue = parseFloat(formData.expectedRevenue) || 0;
    const state = formData.state;
    if (revenue <= 0 || !state) {
       setCalculations(null); setIsCalculating(false); return;
    }
    // Prepare the factors object EXACTLY as needed by calculateDetailedRequirements
    const factorsForCalc = {
      // Pass ALL relevant keys from formData state
      totalAssets: parseFloat(formData.totalAssets) || 0,
      grantFunding: parseFloat(formData.grantFunding) || 0,
      subsector: formData.subsector,
      geographicScope: formData.geographicScope,
      programCount: Number(formData.programCount) || 1,
      staffingModel: formData.staffingModel,
      employeeCount: Number(formData.employeeCount) || 0,
      inventoryType: formData.inventoryType,
      hasInventory: formData.hasInventory, // Use derived value
      fundingSources: formData.fundingSources,
      hasUnrelatedIncome: formData.hasUnrelatedIncome,
      hasForeignAccounts: formData.hasForeignAccounts,
      hasComplexCompensation: formData.hasComplexCompensation,
      hasInvestments: formData.hasInvestments,
      vendorTransactions: formData.vendorTransactions,
      vendorComplexity: formData.vendorComplexity,
      growthStage: formData.growthStage,
      // Add any other factors required by calculateDetailedRequirements
    };

    try {
        const results = calculateDetailedRequirements(
          revenue, [state], factorsForCalc.grantFunding, factorsForCalc
        );
        setCalculations(results); // Store the detailed results
    } catch (error) {
        console.error("Calculation Error:", error); setCalculations(null);
    } finally {
        setIsCalculating(false);
    }
  }, [formData]); // Recalculate whenever formData changes

  // useEffect to trigger calculation automatically (Retained)
  useEffect(() => {
    if (formData.expectedRevenue && formData.state) {
        // Optional debounce could go here
        runCalculation();
    } else {
        setCalculations(null);
    }
  }, [formData.expectedRevenue, formData.state, runCalculation]);

  // Handler for wizard completion (Retained)
  const handleCalculateButtonClick = (finalFactorsFromWizard) => {
     handleFactorChange(finalFactorsFromWizard);
     // Calculation runs via useEffect after state update
  };

  // --- PDF Generation (Retained - ensure pdfCalculations uses correct structure) ---
  const handleGenerateReport = async () => {
    if (!calculations) { alert("Please run calculation first."); return; }
    try {
      // --- Projection logic (kept simple as before, can be improved) ---
      const fiveYearProjection = [];
      const baseRevenue = parseFloat(formData.expectedRevenue) || 0;
      const growthRate = formData.growthStage === 'scaling' ? 0.15 : formData.growthStage === 'growing' ? 0.10 : 0.05;
      const costInflation = 0.03;
      let currentRevenue = baseRevenue;
      let lastOngoingCost = calculations.totalOngoing || 0;
      for (let year = 1; year <= 5; year++) {
          const independentOngoing = (year === 1) ? calculations.totalOngoing : (lastOngoingCost * (1 + costInflation));
          const independentTotalYear = (year === 1) ? calculations.totalFirstYear : independentOngoing;
          const fiscalCost = currentRevenue * 0.05;
          fiveYearProjection.push({
              year, revenue: Math.round(currentRevenue),
              independentCost: Math.round(independentTotalYear),
              fiscalCost: Math.round(fiscalCost),
              savings: Math.round(independentTotalYear - fiscalCost)
          });
          currentRevenue *= (1 + growthRate);
          lastOngoingCost = independentOngoing;
      }
      // --- End Projection Logic ---

      // Prepare data for PDF - *Uses the correct structure from 'calculations'*
      const pdfCalculations = {
          detailedRequirements: calculations, // Pass the full structure
          fiscalSponsorshipTotal: calculations.fiscalSponsorship.totalAnnualFee, // Correct total
          independentCosts: { // Summaries if needed by PDF function
              initial: calculations.startup.total,
              ongoing: calculations.totalOngoing,
              total: calculations.totalFirstYear
          },
          // Correct first year savings calculation
          savings: calculations.totalFirstYear - calculations.fiscalSponsorship.totalAnnualFee,
          adminOverheadPercentage: calculations.annual?.administrativeOverheadPercentage?.percentage,
          fiveYearProjection
      };
      const doc = await generateReport(pdfCalculations, formData);
      doc.save('nonprofit-cost-analysis.pdf');
    } catch (error) { console.error('Error generating PDF:', error); alert('Error generating report.'); }
  };

  // --- Render Function (Retains your UI structure) ---
  return (
    <div className="cost-comparison-wrapper">
      <Helmet><title>Cost Comparison Calculator</title></Helmet>

      <div className="calculator-container">
        <div className="input-section">
          <h2>Enter Your Details</h2>
          <FormInputs 
            formData={formData}
            handleInputChange={handleInputChange}
            stateOptions={stateOptions}
          />
          {/* Complexity Factors Wizard - Needs to be the UPDATED component */}
          <ComplexityFactors
            factors={formData}
            onChange={handleFactorChange}
            onCalculate={handleCalculateButtonClick} // Use button click handler
          />
        </div>

        {/* --- RESULTS SECTION (Structure Retained) --- */}
        {isCalculating && <div className="loading-spinner">Calculating...</div>}

        {/* ** CRITICAL ** This relies on CostBreakdown being updated to handle the full 'calculations' object */}
        {calculations && !isCalculating && (
          <>
            {/* Comparison Grid (Structure Retained) */}
            <div className="comparison-grid">
              <CostBreakdown
                type="Fiscal Sponsorship"
                costs={calculations.fiscalSponsorship} // Pass FS specific part
                // Pass total explicitly ONLY if CostBreakdown absolutely needs it this way
                // total={calculations.fiscalSponsorship.totalAnnualFee}
               />
              <CostBreakdown
                type="Independent 501(c)(3)"
                costs={calculations} // PASS THE *ENTIRE* RESULT OBJECT
                state={formData.state}
                stateRequirements={calculations.stateSpecific?.requirements}
                revenue={parseFloat(formData.expectedRevenue) || 0}
                validationInfo={{ /* Retained */ }}
              />
            </div>

            {/* Savings Highlight (Structure Retained, uses corrected totals) */}
            <div className="savings-highlight">
              <h2>Potential First-Year Savings with Fiscal Sponsorship</h2>
              <div className="savings-amount">
                ${(calculations.totalFirstYear - calculations.fiscalSponsorship.totalAnnualFee).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
            </div>

            {/* Advanced Options Buttons (Structure Retained) */}
            <div className="advanced-options">
              <button className="toggle-projections" onClick={() => setShowProjections(true)}>
                 Show 5-Year Projection Chart
              </button>
              <button className="generate-report" onClick={handleGenerateReport}>
                 Download Detailed Report
              </button>
            </div>

            {/* Cost Chart Modal (Structure Retained, uses corrected totals) */}
            {showProjections && (
              <CostChart
                data={{
                  expectedRevenue: formData.expectedRevenue,
                  state: formData.state,
                  independentCosts: {
                    initial: calculations.startup.total,
                    ongoing: calculations.totalOngoing
                  },
                  fiscalSponsorshipCost: calculations.fiscalSponsorship.totalAnnualFee
                }}
                onClose={() => setShowProjections(false)}
              />
            )}
          </>
        )}

        {/* Methodology Section (Structure Retained) */}
        <div className="methodology-section">
          <h3>Our Methodology</h3>
           <p> {/* Updated text */}
             This calculator uses a hybrid approach: (1) Core administrative overhead is estimated using a percentage model validated against real-world Form 990 data for organizations of similar size, location, and complexity. (2) Specific, discrete costs like legal setup, IRS filings, state registrations, audits, and insurance are estimated based on average professional service fees and known requirements. Fiscal sponsorship is calculated at a standard 5% administrative fee.
          </p>
          <div className="report-link-container">
            <a href="https://causeandimpact-assets.s3.us-east-1.amazonaws.com/calculator-validation-report.html" target="_blank" rel="noopener noreferrer" className="validation-report-link">
              Read Detailed Statistical Validation Report
            </a>
          </div>
        </div>
      </div> {/* End calculator-container */}
    </div> // End cost-comparison-wrapper
  );
};

export default CostCalculatorPage;